/**
 * UTM Parameter Capture & Retrieval
 *
 * Captures UTM params (+ fbclid) from the landing URL and stores them
 * in sessionStorage so they persist across page navigations but reset
 * on new sessions (new tab / direct visit).
 *
 * Usage:
 *   captureUtmParams()          — call once on app mount
 *   getUtmParams()              — returns stored params (or empty object)
 *   getUtmForCheckout()         — flat key/value for Stripe metadata
 *   getUtmForCapi()             — formatted for CAPI custom_data
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const STORAGE_KEY = 'cp_utm';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  landing_page?: string;
}

/**
 * Call once on app mount. Reads UTM params from the current URL
 * and persists them in sessionStorage. Only overwrites if new UTM
 * params are present (so navigating away from the landing page
 * doesn't erase them).
 */
export function captureUtmParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = UTM_KEYS.some(k => params.has(k)) || params.has('fbclid');

    if (!hasUtm) return; // No UTM in URL — keep whatever is stored

    const utm: UtmParams = {};
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    const fbclid = params.get('fbclid');
    if (fbclid) utm.fbclid = fbclid;

    utm.landing_page = window.location.pathname;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // sessionStorage blocked (incognito on some browsers) — silently fail
  }
}

/**
 * Returns stored UTM params, or empty object if none.
 */
export function getUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Returns UTM params as flat string key/value for Stripe metadata.
 * Only includes keys that have values (Stripe metadata values must be strings).
 */
export function getUtmForCheckout(): Record<string, string> {
  const utm = getUtmParams();
  const result: Record<string, string> = {};
  if (utm.utm_source) result.utm_source = utm.utm_source;
  if (utm.utm_medium) result.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) result.utm_campaign = utm.utm_campaign;
  if (utm.utm_content) result.utm_content = utm.utm_content;
  if (utm.utm_term) result.utm_term = utm.utm_term;
  if (utm.fbclid) result.fbclid = utm.fbclid;
  if (utm.landing_page) result.landing_page = utm.landing_page;
  return result;
}

/**
 * Returns UTM data formatted for CAPI custom_data payload.
 */
export function getUtmForCapi(): Record<string, string> | undefined {
  const utm = getUtmParams();
  if (!utm.utm_source && !utm.fbclid) return undefined;
  const result: Record<string, string> = {};
  if (utm.utm_source) result.utm_source = utm.utm_source;
  if (utm.utm_medium) result.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) result.utm_campaign = utm.utm_campaign;
  if (utm.utm_content) result.utm_content = utm.utm_content;
  if (utm.utm_term) result.utm_term = utm.utm_term;
  return result;
}
